import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, PrimaryButton } from "../../components/button";
import { CodeEditor } from "../../components/code-editor";
import { CollectionPickerControl } from "../../components/collection-picker";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DataPickerControl } from "../../components/data-picker";
import { Dropdown, DropdownDivider, DropdownItem } from "../../components/dropdown";
import { Editor, EditorDescription, EditorRow, EditorTitle } from "../../components/editor";
import { NumberInput, TextInput } from "../../components/form";
import { GatewayPipeline } from "../../components/gateway-pipeline";
import { Icon } from "../../components/icon";
import { Content, Section } from "../../components/layout";
import { Link } from "../../components/link";
import { TabControl } from "../../components/tabcontrol";
import { BlockCodeWrapper, CheckWrapper, DashboardTextHeading, Heading, MultilingualCodeWrapper, MutedText, Paragraph } from "../../components/text";
import { Warning } from "../../components/warn";
import { getSession } from "../../session";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { copyToClipboard } from "../../ui/clipboard";
import { formatFloat, formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { ellipsis, valueFrom } from "../../util";

const QUERY_STRATEGIES = [
    { value: "Plain", text: "Plain" },
    { value: "Concatenate", text: "Concatenate" },
    { value: "FullRewrite", text: "Full rewrite" },
    { value: "UserRewrite", text: "User rewrite" },
    { value: "QueryFunction", text: "Query function" }
];

const REASONING_EFFORTS = [
    { value: null, text: "Let the provider decide" },
    { value: "minimal", text: "Minimal" },
    { value: "low", text: "Low" },
    { value: "medium", text: "Medium" },
    { value: "high", text: "High" },
    { value: "none", text: "None" }
];

function enumValue(value, fallback) {
    return Array.isArray(value) ? value[0] : (value ?? fallback);
}

function validBaseAddress(value) {
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

export default function (ctx) {

    const gatewayId = ctx.params.gatewayId;
    const isCreatingGateway = gatewayId == "create";
    const gateway = el.state.create(null);

    function updateView() {
        runOperation("Fetching gateway...", async () => {
            const response = await api.getJson(`/api/v1/ai-gateways/${gatewayId}`);
            gateway.value = response.data;
        });
    }

    if (!isCreatingGateway) {
        updateView();
    }

    function getGatewayData() {
        return {
            name: valueFrom("gatewayName"),
            parameters: {
                baseAddress: valueFrom("gatewayBaseAddress"),
                modelName: valueFrom("gatewayModelName"),
                apiKey: valueFrom("gatewayApiKey")?.nullWhenEmpty(),
                knowledgeCollections: valueFrom("gatewayCollections") || [],
                knowledgeBaseMaximumResults: valueFrom("gatewayRetrievalMaximumCount"),
                knowledgeBaseMinimumScore: valueFrom("gatewayRetrievalMinimumScore"),
                knowledgeUseReferences: valueFrom("gatewayKnowledgeUseReferences"),
                knowledgeUseMetaDescriptions: valueFrom("gatewayKnowledgeUseMetaDescriptions"),
                queryStrategy: valueFrom("gatewayQueryStrategy"),
                queryStrategyParameters: {
                    concatenateContextSize: valueFrom("gatewayConcatenateSize"),
                    rewriteContextSize: valueFrom("gatewayRewriteSize")
                },
                systemInstruction: valueFrom("gatewaySystemInstructions")?.nullWhenEmpty(),
                temperature: valueFrom("gatewayTemperature"),
                topP: valueFrom("gatewayTopP"),
                presencePenalty: valueFrom("gatewayPresencePenalty"),
                frequencyPenalty: valueFrom("gatewayFrequencyPenalty"),
                stop: valueFrom("gatewayStopSentence")?.nullWhenEmpty(),
                maxCompletionTokens: valueFrom("gatewayMaxCompletionTokens"),
                reasoningEffort: valueFrom("gatewayReasoningEffort")
            }
        };
    }

    function handleSave() {
        const data = getGatewayData();

        runOperation("Saving gateway...", async () => {
            if (isCreatingGateway) {
                const response = await api.postJson("/api/v1/ai-gateways", data);
                page(`/dashboard/gateways/${response.data.aiGatewayId}`);
            } else {
                await api.patchJson(`/api/v1/ai-gateways/${gatewayId}`, data);
                updateView();
            }
        });
    }

    return el.state.listening([gateway], () =>
        Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                    onClick() {
                        history.go(-1);
                        page();
                    }
                })
            ),
            DashboardTitle(isCreatingGateway ? "New AI gateway" :
                DashboardTextHeading(
                    el.fragment(gateway.value?.name, MutedText(" ~#", gatewayId.slice(-6))), "AI Gateway details")),
            Section(
                CommandBar(
                    PrimaryButton(
                        Icon("save-line"),
                        isCreatingGateway ? "Create AI gateway" : "Save changes",
                        {
                            onClick: handleSave
                        }
                    ),
                    !isCreatingGateway && Dropdown("Options",
                        DropdownItem("View integration code", Icon("braces-line"), {
                            onClick() {
                                displayAlert({
                                    title: "Gateway code",
                                    dialogSize: "medium",
                                    message: el.fragment(
                                        "Use the code below to call this gateway through the OpenAI-compatible API.",
                                        MultilingualCodeWrapper("Example", [
                                            {
                                                label: "Python",
                                                language: "python",
                                                code: `
import os
from openai import OpenAI

client = OpenAI(
    base_url="${window.env.apiEndpoint}v1",
    api_key=os.environ["COMPOUND_API_KEY"],
)

completion = client.chat.completions.create(
    model="${gateway.value?.slug}",
    messages=[{"role": "user", "content": "What is the capital of France?"}],
)

print(completion.choices[0].message)
`
                                            },
                                            {
                                                label: "Curl",
                                                language: "bash",
                                                code: `
curl ${window.env.apiEndpoint}v1/chat/completions \\
    -H "Authorization: Bearer $COMPOUND_API_KEY" \\
    -H 'Content-Type: application/json' \\
    -d '{
        "model": "${gateway.value?.slug}",
        "messages": [{"role": "user", "content": "What is the capital of France?"}],
        "stream": false
    }'
`
                                            }
                                        ])
                                    )
                                })
                            }
                        }),
                        DropdownItem("Copy gateway slug", Icon("file-copy-line"), {
                            onClick() {
                                copyToClipboard(gateway.value?.slug);
                            }
                        }),
                        DropdownItem("Copy ID", Icon("file-copy-line"), {
                            onClick() {
                                copyToClipboard(gatewayId);
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Export to JSON", Icon("upload-2-line"), {
                            onClick() {
                                displayAlert({
                                    title: "Export gateway as JSON",
                                    dialogSize: "large",
                                    message: BlockCodeWrapper("gateway.json", JSON.stringify(getGatewayData(), null, 4), "json")
                                })
                            }
                        }),
                        DropdownDivider(),
                        DropdownItem("Delete gateway", Icon("delete-bin-line"), {
                            onClick() {
                                displayAlert({
                                    title: "Delete AI gateway",
                                    message: "Are you sure you want to delete this AI gateway? This action cannot be undone.",
                                    type: "alert-warning",
                                    buttons: [
                                        {
                                            label: "Delete AI gateway",
                                            onClick(event) {
                                                runOperation("Deleting gateway...", async () => {
                                                    await api.deleteJson(`/api/v1/ai-gateways/${gatewayId}`);
                                                    page("/dashboard/ai-gateways");
                                                });
                                                event.target.closest("dialog").dismiss();
                                            }
                                        },
                                        {
                                            label: "Cancel",
                                            onClick(event) {
                                                event.target.closest("dialog").dismiss();
                                            }
                                        }
                                    ]
                                });
                            }
                        })
                    )
                )
            ),
            Section(
                TabControl([
                    {
                        title: "Gateway",
                        icon: Icon("route-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "gatewayName",
                                        value: gateway.value?.name,
                                        renderValue(value) {
                                            return Button(value || Warning("Not set"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Gateway name", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.length > 3, "The gateway name must be at least 3 characters long."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Gateway name"),
                                        Paragraph("Friendly name used to identify this gateway.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayModelName",
                                        value: gateway.value?.parameters.modelName,
                                        renderValue(value) {
                                            return Button(value || Warning("Not set"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "gpt-4.1-mini", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x.trim().length > 0, "Model name cannot be empty."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Model name"),
                                        Paragraph("Model identifier sent to your BYOK provider.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayBaseAddress",
                                        value: gateway.value?.parameters.baseAddress,
                                        renderValue(value) {
                                            return Button(value || Warning("Not set"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "https://api.openai.com/v1/", { value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [validBaseAddress, "Base address must be an absolute http or https URL."]
                                                ]
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("BYOK base address"),
                                        Paragraph("OpenAI-compatible endpoint for your provider. Integrated inference is not available in this reduced server.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayApiKey",
                                        value: gateway.value?.parameters.apiKey,
                                        renderValue(value) {
                                            return Button(value ? ellipsis(value, 12) : MutedText("Not set"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "sk-...", { value }),
                                                onConfirm: x => callback(x.nullWhenEmpty()),
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("API key"),
                                        Paragraph("Provider key used only by this gateway.")
                                    )
                                )
                            ),
                            Section(
                                el.state.listening([gateway], () =>
                                    GatewayPipeline(getGatewayData())
                                )
                            )
                        )
                    },
                    {
                        title: "RAG",
                        icon: Icon("book-shelf-line"),
                        contents: Content(
                            Heading("RAG settings", "Choose collections and how documents are retrieved before inference."),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayCollections",
                                        value: gateway.value?.parameters.knowledgeCollections || [],
                                        renderValue(value) {
                                            return Button(value?.length ? `${formatNumber(value.length)} collection(s)` : MutedText("No collections"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: CollectionPickerControl({ value, multiple: true, allowNone: true }),
                                                dialogSize: "large",
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Knowledge collections"),
                                        Paragraph("Collections used as context for this gateway.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayQueryStrategy",
                                        value: enumValue(gateway.value?.parameters.queryStrategy, "Plain"),
                                        renderValue(value) {
                                            return Button(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({ value, data: QUERY_STRATEGIES }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Query strategy"),
                                        Paragraph("How the user message is transformed into RAG searches.")
                                    )
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayRetrievalMaximumCount",
                                        value: gateway.value?.parameters.knowledgeBaseMaximumResults ?? 5,
                                        renderValue(value) {
                                            return Button(formatNumber(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 999, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Maximum results"),
                                        Paragraph("Maximum number of documents retrieved per search.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayRetrievalMinimumScore",
                                        value: gateway.value?.parameters.knowledgeBaseMinimumScore ?? 0.2,
                                        renderValue(value) {
                                            return Button(formatFloat(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 1, step: 0.05 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Minimum score"),
                                        Paragraph("Lowest vector match score accepted in the context.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayKnowledgeUseReferences",
                                        value: gateway.value?.parameters.knowledgeUseReferences ?? false,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Use references"),
                                        Paragraph("Include referenced chunks when a matching document points to related content.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayKnowledgeUseMetaDescriptions",
                                        value: gateway.value?.parameters.knowledgeUseMetaDescriptions ?? true,
                                        renderValue(value) {
                                            return CheckWrapper(value, "enabled", "disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Use meta descriptions"),
                                        Paragraph("Include collection context and metadata when presenting retrieved documents.")
                                    )
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayConcatenateSize",
                                        value: gateway.value?.parameters.queryStrategyParameters?.concatenateContextSize ?? 3,
                                        renderValue(value) {
                                            return Button(formatNumber(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 20, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Concatenate context size"),
                                        Paragraph("Number of recent messages used by concatenate-based strategies.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayRewriteSize",
                                        value: gateway.value?.parameters.queryStrategyParameters?.rewriteContextSize ?? 3,
                                        renderValue(value) {
                                            return Button(formatNumber(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 20, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Rewrite context size"),
                                        Paragraph("Number of recent messages used by rewrite-based strategies.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Inference",
                        icon: Icon("equalizer-2-line"),
                        contents: Content(
                            Heading("Inference options", "Tune the request sent to the BYOK provider."),
                            Section(
                                Editor(
                                    {
                                        id: "gatewaySystemInstructions",
                                        value: gateway.value?.parameters.systemInstruction,
                                        renderValue(value) {
                                            return Button(value ? ellipsis(value, 80) : MutedText("None"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: CodeEditor({ value }),
                                                dialogSize: "large",
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("System instruction"),
                                        Paragraph("System prompt prepended to gateway chats.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "gatewayReasoningEffort",
                                        value: gateway.value?.parameters.reasoningEffort,
                                        renderValue(value) {
                                            return Button(value || MutedText("Provider default"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({ value, data: REASONING_EFFORTS, allowNone: true }),
                                                onConfirm: callback,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Reasoning effort"),
                                        Paragraph("Optional reasoning-effort hint for providers that support it.")
                                    )
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayTemperature",
                                        value: gateway.value?.parameters.temperature,
                                        renderValue(value) {
                                            return Button(value ?? MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value: value ?? 0, min: -2, max: 2, step: 0.1 }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Temperature"))
                                ),
                                Editor(
                                    {
                                        id: "gatewayTopP",
                                        value: gateway.value?.parameters.topP,
                                        renderValue(value) {
                                            return Button(value ?? MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value: value ?? 1, min: -2, max: 2, step: 0.1 }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Top P"))
                                ),
                                Editor(
                                    {
                                        id: "gatewayPresencePenalty",
                                        value: gateway.value?.parameters.presencePenalty,
                                        renderValue(value) {
                                            return Button(value ?? MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value: value ?? 0, min: -2, max: 2, step: 0.1 }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Presence penalty"))
                                ),
                                Editor(
                                    {
                                        id: "gatewayFrequencyPenalty",
                                        value: gateway.value?.parameters.frequencyPenalty,
                                        renderValue(value) {
                                            return Button(value ?? MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value: value ?? 0, min: -2, max: 2, step: 0.1 }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Frequency penalty"))
                                )
                            ),
                            Section(
                                Editor(
                                    {
                                        id: "gatewayMaxCompletionTokens",
                                        value: gateway.value?.parameters.maxCompletionTokens,
                                        renderValue(value) {
                                            return Button(value ? formatNumber(value) : MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value: value ?? 1024, min: 1, max: 1000000, step: 1 }),
                                                onConfirm: callback,
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Max completion tokens"))
                                ),
                                Editor(
                                    {
                                        id: "gatewayStopSentence",
                                        value: gateway.value?.parameters.stop,
                                        renderValue(value) {
                                            return Button(value || MutedText("Unset"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Stop sequence", { value }),
                                                onConfirm: x => callback(x.nullWhenEmpty()),
                                                nullButton: true,
                                                validate: []
                                            })
                                        }
                                    },
                                    EditorDescription(EditorTitle("Stop sequence"))
                                ),
                                EditorRow(
                                    Paragraph("This reduced UI only stores settings supported by the current server contract.")
                                )
                            )
                        )
                    }
                ])
            )
        )
    )
}
